import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import { GridFilterInputValueProps } from "@mui/x-data-grid-pro";
import ZoneAutocomplete from "@app.automotus.io/components/ZoneAutocomplete";

export interface NamedAutocompleteOption {
  value: string;
  label: string;
}

interface MultiSelectAutocompleteFilterValueProps extends GridFilterInputValueProps {
  label: string;
  options: NamedAutocompleteOption[];
  // Overrides MUI Autocomplete's default "No options" -- passed when options is empty because
  // the underlying options query errored, not because there genuinely are none, so the officer
  // isn't left thinking a client with real areas just has none.
  noOptionsText?: string;
}

interface ZoneFilterValueProps extends GridFilterInputValueProps {
  payeeAccountId: string;
}

// Single-select -- used by the Zone filter, which pinpoints one exact location. Adapts the
// DataGrid filter-input contract (item/applyValue) to the shared, server-searching
// ZoneAutocomplete. One zone can have several records (one per camera), so `item.value` holds all
// their ids comma-joined, for the same URL reason as MultiSelectAutocompleteFilterValue below.
export const ZoneFilterValue: React.FC<ZoneFilterValueProps> = ({
  item,
  applyValue,
  focusElementRef,
  payeeAccountId,
}) => {
  // Memoized so ZoneAutocomplete gets a stable array, not a fresh one every render.
  const zoneIds = React.useMemo(
    () => (typeof item.value === "string" && item.value.length > 0 ? item.value.split(",") : []),
    [item.value],
  );

  return (
    <ZoneAutocomplete
      payeeAccountId={payeeAccountId}
      value={zoneIds}
      onChange={(newZoneIds) => applyValue({ ...item, value: newZoneIds.join(",") || undefined })}
      label="Zone"
      variant="standard"
      inputRef={focusElementRef}
    />
  );
};

// Multi-select (checkboxes) -- used by the Area filter, since an officer can be assigned more
// than one coverage area at once (AUT-5863, per Luca: "Terminal B UL East and West at the same
// time"). `item.value` is a comma-joined string of selected option values, not a real array --
// this app's own filter-URL persistence (filterModelToSearchParams/parseFilterModelFromSearchParams
// in common/graphql/queryString) round-trips every filter value through `encodeURIComponent`/
// `decodeURIComponent` as a single string, so a real array value would silently degrade to its
// stringified form on the very first page reload or shared URL. Encoding/decoding the join
// ourselves keeps this working within that existing constraint rather than fighting it.
export const MultiSelectAutocompleteFilterValue: React.FC<MultiSelectAutocompleteFilterValueProps> = ({
  item,
  applyValue,
  label,
  options,
  focusElementRef,
  noOptionsText,
}) => {
  const selectedValues = typeof item.value === "string" && item.value.length > 0 ? item.value.split(",") : [];
  const selectedOptions = options.filter((option) => selectedValues.includes(option.value));

  return (
    <Autocomplete<NamedAutocompleteOption, true>
      multiple
      disableCloseOnSelect
      options={options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      value={selectedOptions}
      onChange={(_event, newValue) => {
        applyValue({ ...item, value: newValue.map((option) => option.value).join(",") || undefined });
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox checked={selected} sx={{ marginRight: 1 }} />
          {option.label}
        </li>
      )}
      noOptionsText={noOptionsText}
      renderInput={(params) => <TextField {...params} inputRef={focusElementRef} variant="standard" label={label} />}
    />
  );
};

export interface StreetAddressWhereClause {
  sessions: { street_address: { _in: string[] } };
}

// Highest camera number on any zone in prod is 7 (2026-11-02).
const MAX_CAMERA_NUMBER = 9;

// The Zone and Area filters match a notice by the zone names recorded on its sessions (AUT-6037).
// Some notices record a camera name instead, which is the zone name plus " (N)" (AUT-6166).
// Stopgap until sessions store a zone id.
export const buildStreetAddressWhereClause = (zoneNames: string[]): StreetAddressWhereClause => ({
  sessions: {
    street_address: {
      _in: zoneNames.flatMap((name) => [
        name,
        ...Array.from({ length: MAX_CAMERA_NUMBER }, (_, index) => `${name} (${index + 1})`),
      ]),
    },
  },
});
